// Server calls for the composer and list sends. Keys sit under "inbox" so the server's live refreshes reach them;
// templates keep their own key, which the server and the bin refresh.

import { api, ApiError } from '@/lib/api';
import type {
  ComposeInfo, ComposeInput, ComposeResult, ComposeTarget, ContactSuggestion, ListSendDetail, ListSendInput, ListSendItem, ListSendPreview,
  ListSendRoom, OutboxItem, ReplyContext, ScheduledEntry, Template, UploadedFile,
} from '@shared/mail';
import type { ApiErrorBody } from '@shared/types';

export const keys = {
  info: ['inbox', 'compose', 'info'] as const,
  templates: ['templates'] as const,
  contacts: (q: string) => ['inbox', 'compose', 'contacts', q] as const,
  recipients: (emails: string[]) => ['inbox', 'compose', 'recipients', emails.join(',')] as const,
  target: (personId?: string, companyId?: string, dealId?: string) => ['inbox', 'compose', 'target', personId ?? '', companyId ?? '', dealId ?? ''] as const,
  reply: (threadId: string, emailId: string | undefined, mode: string) => ['inbox', 'compose', 'reply', threadId, emailId ?? '', mode] as const,
  outbox: (id: string) => ['inbox', 'compose', 'outbox', id] as const,
  listPreview: (ids: string[]) => ['inbox', 'compose', 'list-preview', ids.join(',')] as const,
  listSend: (id: string) => ['inbox', 'compose', 'list-send', id] as const,
  listRoom: (mailboxId: string, startAt: string) => ['inbox', 'compose', 'list-room', mailboxId, startAt] as const,
  running: ['inbox', 'compose', 'running'] as const,
};

export const composeInfoQuery = { queryKey: keys.info, queryFn: () => api.get<ComposeInfo>('/mail/compose-info'), staleTime: 30_000 };
export const templatesQuery = { queryKey: keys.templates, queryFn: () => api.get<Template[]>('/mail/templates'), staleTime: 60_000 };

export function fetchContacts(q: string, signal?: AbortSignal): Promise<ContactSuggestion[]> {
  return api.get<ContactSuggestion[]>('/mail/contacts', { query: { q }, signal });
}

export function checkRecipients(emails: string[]): Promise<ContactSuggestion[]> {
  return emails.length ? api.post<ContactSuggestion[]>('/mail/recipients', { emails }) : Promise.resolve([]);
}

export function fetchTarget(personId?: string, companyId?: string, dealId?: string): Promise<ComposeTarget> {
  return api.get<ComposeTarget>('/mail/compose-target', { query: { personId, companyId, dealId } });
}

export function timeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function fetchReplyContext(threadId: string, emailId: string | undefined, mode: 'reply' | 'replyAll' | 'forward'): Promise<ReplyContext> {
  return api.get<ReplyContext>('/mail/reply-context', { query: { threadId, emailId, mode, tz: timeZone() } });
}

export const fetchOutbox = (id: string) => api.get<OutboxItem>(`/mail/outbox/${id}`);
export const sendEmail = (input: ComposeInput) => api.post<ComposeResult>('/mail/send', input);
export const cancelOutbox = (ids: string[]) => api.post<OutboxItem[]>('/mail/outbox/cancel', { ids });
export const saveTemplate = (input: { name: string; subject: string; body: string }) => api.post<Template>('/mail/templates', input);
export const removeUpload = (id: string) => api.del<void>(`/mail/uploads/${id}`);

export const previewListSend = (personIds: string[]) => api.post<ListSendPreview>('/mail/list-sends/preview', { personIds });
export const startListSend = (input: ListSendInput) => api.post<ListSendItem>('/mail/list-sends', input);
export const fetchListSend = (id: string) => api.get<ListSendDetail>(`/mail/list-sends/${id}`);
export const fetchListRoom = (mailboxId: string, startAt: string) => api.get<ListSendRoom>('/mail/list-sends/room', { query: { mailboxId, startAt } });
export const cancelListSend = (id: string) => api.post<ListSendItem>(`/mail/list-sends/${id}/cancel`);
export const fetchScheduled = () => api.get<ScheduledEntry[]>('/inbox/scheduled');

export interface UploadHandle {
  promise: Promise<UploadedFile>;
  abort: () => void;
}

/**
 * Uploads one file as raw bytes, reporting progress from 0 to 1. The name and type travel in the address, so the
 * server's JSON reader leaves the body alone whatever the file is.
 */
export function uploadFile(file: File, onProgress: (fraction: number) => void): UploadHandle {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<UploadedFile>((resolve, reject) => {
    const params = new URLSearchParams({ name: file.name || 'Attachment' });
    if (file.type) params.set('mime', file.type);
    xhr.open('POST', `/api/mail/uploads?${params.toString()}`);
    xhr.setRequestHeader('x-clientbook', '1');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data: unknown = null;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        data = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as UploadedFile);
      else {
        const e = (data as ApiErrorBody | null)?.error;
        reject(new ApiError(xhr.status, e?.message ?? `The upload failed (${xhr.status}).`, e?.code ?? 'error', e?.details));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, 'Clientbook cannot reach the server. Check your connection and try again.', 'offline'));
    xhr.onabort = () => reject(new ApiError(0, 'Upload stopped.', 'aborted'));
    xhr.send(file);
  });
  return { promise, abort: () => xhr.abort() };
}
